export type EffortCalculationMode = 'manual' | 'point_based' | 'schedule_based' | 'hybrid';

export type IntensityFactorKey = 'difficulty' | 'labor_intensive' | 'travel_intensive' | 'remote_support' | 'cost_sensitive';

export interface IntensityFactor {
  key: IntensityFactorKey;
  label: string;
  description: string;
  multiplier: number;
  enabled: boolean;
}

export interface WorkingHoursConfig {
  standard_day_hours: number;
  break_minutes_per_day: number;
  overtime_enabled: boolean;
  overtime_multiplier: number;
  overtime_threshold_hours: number;
}

export interface EffortScalePoint {
  points: number;
  hours: number;
  label: string;
  description?: string;
  difficulty_level?: number;
  labor_intensity?: number;
  travel_intensity?: number;
  remote_intensity?: number;
  cost_impact?: 'low' | 'medium' | 'high' | 'critical';
  min_hours?: number;
  max_hours?: number;
}

export interface EffortConfig {
  calculation_mode: EffortCalculationMode;
  auto_update_estimated_hours: boolean;
  point_label: string;
  hour_label: string;
  point_scale: EffortScalePoint[];
  intensity_factors: IntensityFactor[];
  working_hours: WorkingHoursConfig;
  schedule_weight_in_hybrid: number;
  require_exact_schedule_match: boolean;
  allow_manual_override: boolean;
}

export const DEFAULT_INTENSITY_FACTORS: IntensityFactor[] = [
  { key: 'difficulty', label: 'Difficulty', description: 'Technical complexity and problem-solving required', multiplier: 1.0, enabled: true },
  { key: 'labor_intensive', label: 'Labor Intensive', description: 'Physical effort and manpower requirements', multiplier: 1.0, enabled: true },
  { key: 'travel_intensive', label: 'Travel Intensive', description: 'Significant travel time and logistics', multiplier: 1.0, enabled: true },
  { key: 'remote_support', label: 'Remote Support', description: 'Remote/diagnostic work complexity', multiplier: 1.0, enabled: true },
  { key: 'cost_sensitive', label: 'Cost Sensitive', description: 'Budget and cost impact considerations', multiplier: 1.0, enabled: true },
];

export const DEFAULT_WORKING_HOURS: WorkingHoursConfig = {
  standard_day_hours: 8,
  break_minutes_per_day: 60,
  overtime_enabled: false,
  overtime_multiplier: 1.5,
  overtime_threshold_hours: 8,
};

export const DEFAULT_EFFORT_POINT_SCALE: EffortScalePoint[] = [
  { points: 1, hours: 0.5, label: 'Quick win', description: 'Small task or check-in that can be wrapped up fast.', difficulty_level: 1, labor_intensity: 1, travel_intensity: 1, remote_intensity: 1, cost_impact: 'low', min_hours: 0.25, max_hours: 1 },
  { points: 2, hours: 1, label: 'Light effort', description: 'Straightforward work with little coordination required.', difficulty_level: 2, labor_intensity: 2, travel_intensity: 1, remote_intensity: 2, cost_impact: 'low', min_hours: 0.5, max_hours: 2 },
  { points: 3, hours: 2, label: 'Standard task', description: 'A normal piece of work with a few moving parts.', difficulty_level: 3, labor_intensity: 3, travel_intensity: 2, remote_intensity: 3, cost_impact: 'medium', min_hours: 1, max_hours: 4 },
  { points: 5, hours: 4, label: 'Complex task', description: 'Needs focused execution, prep, or follow-up.', difficulty_level: 5, labor_intensity: 4, travel_intensity: 3, remote_intensity: 4, cost_impact: 'medium', min_hours: 2, max_hours: 6 },
  { points: 8, hours: 8, label: 'Full-day item', description: 'Likely a day of work for one person or crew.', difficulty_level: 6, labor_intensity: 6, travel_intensity: 4, remote_intensity: 5, cost_impact: 'high', min_hours: 4, max_hours: 12 },
  { points: 13, hours: 16, label: 'Multi-day effort', description: 'Requires extended work over multiple sessions.', difficulty_level: 8, labor_intensity: 7, travel_intensity: 5, remote_intensity: 6, cost_impact: 'high', min_hours: 8, max_hours: 24 },
  { points: 21, hours: 32, label: 'Major undertaking', description: 'Large delivery needing significant planning or coordination.', difficulty_level: 10, labor_intensity: 9, travel_intensity: 6, remote_intensity: 7, cost_impact: 'critical', min_hours: 16, max_hours: 48 },
];

export const DEFAULT_EFFORT_CONFIG: EffortConfig = {
  calculation_mode: 'point_based',
  auto_update_estimated_hours: true,
  point_label: 'pts',
  hour_label: 'hrs',
  point_scale: DEFAULT_EFFORT_POINT_SCALE,
  intensity_factors: DEFAULT_INTENSITY_FACTORS,
  working_hours: DEFAULT_WORKING_HOURS,
  schedule_weight_in_hybrid: 0.5,
  require_exact_schedule_match: false,
  allow_manual_override: true,
};

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeScalePoint(point: Partial<EffortScalePoint>, fallbackIndex: number): EffortScalePoint | null {
  const points = toFiniteNumber(point.points);
  const hours = toFiniteNumber(point.hours);

  if (points === null || points <= 0 || hours === null || hours < 0) {
    return null;
  }

  const safeLabel = typeof point.label === 'string' && point.label.trim()
    ? point.label.trim()
    : `Level ${fallbackIndex + 1}`;

  const safeDescription = typeof point.description === 'string' && point.description.trim()
    ? point.description.trim()
    : undefined;

  const difficultyLevel = toFiniteNumber(point.difficulty_level);
  const laborIntensity = toFiniteNumber(point.labor_intensity);
  const travelIntensity = toFiniteNumber(point.travel_intensity);
  const remoteIntensity = toFiniteNumber(point.remote_intensity);
  const minHours = toFiniteNumber(point.min_hours);
  const maxHours = toFiniteNumber(point.max_hours);

  const validCostImpact = ['low', 'medium', 'high', 'critical'].includes(point.cost_impact as string)
    ? point.cost_impact as 'low' | 'medium' | 'high' | 'critical'
    : undefined;

  return {
    points,
    hours: Math.round(hours * 100) / 100,
    label: safeLabel,
    ...(safeDescription ? { description: safeDescription } : {}),
    ...(difficultyLevel !== null ? { difficulty_level: Math.min(10, Math.max(1, difficultyLevel)) } : {}),
    ...(laborIntensity !== null ? { labor_intensity: Math.min(10, Math.max(1, laborIntensity)) } : {}),
    ...(travelIntensity !== null ? { travel_intensity: Math.min(10, Math.max(1, travelIntensity)) } : {}),
    ...(remoteIntensity !== null ? { remote_intensity: Math.min(10, Math.max(1, remoteIntensity)) } : {}),
    ...(validCostImpact ? { cost_impact: validCostImpact } : {}),
    ...(minHours !== null && minHours >= 0 ? { min_hours: Math.round(minHours * 100) / 100 } : {}),
    ...(maxHours !== null && maxHours >= 0 ? { max_hours: Math.round(maxHours * 100) / 100 } : {}),
  };
}

function normalizeIntensityFactor(factor: Partial<IntensityFactor>): IntensityFactor | null {
  const validKeys: IntensityFactorKey[] = ['difficulty', 'labor_intensive', 'travel_intensive', 'remote_support', 'cost_sensitive'];

  if (!factor.key || !validKeys.includes(factor.key as IntensityFactorKey)) {
    return null;
  }

  const multiplier = toFiniteNumber(factor.multiplier);
  if (multiplier === null || multiplier < 0) {
    return null;
  }

  return {
    key: factor.key as IntensityFactorKey,
    label: typeof factor.label === 'string' && factor.label.trim() ? factor.label.trim() : factor.key,
    description: typeof factor.description === 'string' ? factor.description : '',
    multiplier: Math.round(multiplier * 100) / 100,
    enabled: factor.enabled !== false,
  };
}

function normalizeWorkingHours(wh: Partial<WorkingHoursConfig>): WorkingHoursConfig {
  const standardDayHours = toFiniteNumber(wh.standard_day_hours);
  const breakMinutes = toFiniteNumber(wh.break_minutes_per_day);
  const overtimeMultiplier = toFiniteNumber(wh.overtime_multiplier);
  const overtimeThreshold = toFiniteNumber(wh.overtime_threshold_hours);

  return {
    standard_day_hours: standardDayHours !== null && standardDayHours > 0 ? standardDayHours : DEFAULT_WORKING_HOURS.standard_day_hours,
    break_minutes_per_day: breakMinutes !== null && breakMinutes >= 0 ? breakMinutes : DEFAULT_WORKING_HOURS.break_minutes_per_day,
    overtime_enabled: wh.overtime_enabled === true,
    overtime_multiplier: overtimeMultiplier !== null && overtimeMultiplier > 0 ? overtimeMultiplier : DEFAULT_WORKING_HOURS.overtime_multiplier,
    overtime_threshold_hours: overtimeThreshold !== null && overtimeThreshold > 0 ? overtimeThreshold : DEFAULT_WORKING_HOURS.overtime_threshold_hours,
  };
}

export function normalizeEffortConfig(rawConfig: unknown): EffortConfig {
  const parsed = typeof rawConfig === 'string'
    ? (() => {
      try {
        return JSON.parse(rawConfig);
      } catch {
        return null;
      }
    })()
    : rawConfig;

  const candidate = (parsed && typeof parsed === 'object' ? parsed : {}) as Partial<EffortConfig>;
  const rawScale = Array.isArray(candidate.point_scale) ? candidate.point_scale : DEFAULT_EFFORT_POINT_SCALE;
  const rawIntensityFactors = Array.isArray(candidate.intensity_factors) ? candidate.intensity_factors : DEFAULT_INTENSITY_FACTORS;

  const dedupedScale = new Map<number, EffortScalePoint>();
  rawScale.forEach((point, index) => {
    const normalized = normalizeScalePoint(point as Partial<EffortScalePoint>, index);
    if (normalized) {
      dedupedScale.set(normalized.points, normalized);
    }
  });

  const pointScale = Array.from(dedupedScale.values()).sort((a, b) => a.points - b.points);

  const normalizedIntensityFactors = rawIntensityFactors
    .map((f) => normalizeIntensityFactor(f as Partial<IntensityFactor>))
    .filter((f): f is IntensityFactor => f !== null);

  const validCalculationModes: EffortCalculationMode[] = ['manual', 'point_based', 'schedule_based', 'hybrid'];
  const calculationMode = validCalculationModes.includes(candidate.calculation_mode as EffortCalculationMode)
    ? candidate.calculation_mode as EffortCalculationMode
    : DEFAULT_EFFORT_CONFIG.calculation_mode;

  return {
    calculation_mode: calculationMode,
    auto_update_estimated_hours: candidate.auto_update_estimated_hours !== false,
    point_label: typeof candidate.point_label === 'string' && candidate.point_label.trim() ? candidate.point_label.trim() : DEFAULT_EFFORT_CONFIG.point_label,
    hour_label: typeof candidate.hour_label === 'string' && candidate.hour_label.trim() ? candidate.hour_label.trim() : DEFAULT_EFFORT_CONFIG.hour_label,
    point_scale: pointScale.length > 0 ? pointScale : DEFAULT_EFFORT_POINT_SCALE,
    intensity_factors: normalizedIntensityFactors.length > 0 ? normalizedIntensityFactors : DEFAULT_INTENSITY_FACTORS,
    working_hours: normalizeWorkingHours(candidate.working_hours || {}),
    schedule_weight_in_hybrid: typeof candidate.schedule_weight_in_hybrid === 'number' && candidate.schedule_weight_in_hybrid >= 0 && candidate.schedule_weight_in_hybrid <= 1
      ? candidate.schedule_weight_in_hybrid
      : DEFAULT_EFFORT_CONFIG.schedule_weight_in_hybrid,
    require_exact_schedule_match: candidate.require_exact_schedule_match === true,
    allow_manual_override: candidate.allow_manual_override !== false,
  };
}

export function getEffortScaleEntry(config: EffortConfig | null | undefined, points: number | null | undefined): EffortScalePoint | null {
  if (!config || points === null || points === undefined) {
    return null;
  }

  return config.point_scale.find((entry) => entry.points === points) || null;
}

export function getEstimatedHoursForPoints(config: EffortConfig | null | undefined, points: number | null | undefined): number | null {
  const entry = getEffortScaleEntry(config, points);
  return entry ? entry.hours : null;
}

export function getNextEffortScalePoint(config: EffortConfig | null | undefined): EffortScalePoint {
  const normalized = normalizeEffortConfig(config);
  const usedPoints = normalized.point_scale.map((entry) => entry.points);
  const defaultCandidate = DEFAULT_EFFORT_POINT_SCALE.find((entry) => !usedPoints.includes(entry.points));

  if (defaultCandidate) {
    return { ...defaultCandidate };
  }

  const lastEntry = normalized.point_scale[normalized.point_scale.length - 1] || DEFAULT_EFFORT_POINT_SCALE[DEFAULT_EFFORT_POINT_SCALE.length - 1];
  const nextPoints = Math.max(1, Math.round(lastEntry.points + 1));
  return {
    points: nextPoints,
    hours: lastEntry.hours,
    label: `Level ${normalized.point_scale.length + 1}`,
    description: '',
    difficulty_level: 5,
    labor_intensity: 5,
    travel_intensity: 5,
    remote_intensity: 5,
    cost_impact: 'medium',
    min_hours: lastEntry.hours,
    max_hours: lastEntry.hours * 2,
  };
}

export function calculateEstimatedHoursFromSchedule(
  config: EffortConfig,
  scheduledStart: string | null | undefined,
  scheduledEnd: string | null | undefined,
): number | null {
  if (!scheduledStart || !scheduledEnd) {
    return null;
  }

  const start = new Date(scheduledStart);
  const end = new Date(scheduledEnd);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return null;
  }

  const durationMs = end.getTime() - start.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);

  const wh = config.working_hours;
  let effectiveHours = durationHours;

  if (wh.overtime_enabled && durationHours > wh.overtime_threshold_hours) {
    const regularHours = wh.overtime_threshold_hours;
    const overtimeHours = durationHours - wh.overtime_threshold_hours;
    effectiveHours = regularHours + (overtimeHours * wh.overtime_multiplier);
  }

  return Math.round(effectiveHours * 100) / 100;
}

export function calculateEstimatedHoursFromChildren(
  config: EffortConfig,
  children: Array<{ scheduled_start?: string | null; scheduled_end?: string | null; assigned_users?: number[] | null } | null> | null | undefined,
): number | null {
  if (!children || children.length === 0) {
    return null;
  }

  let totalManHours = 0;

  for (const child of children) {
    if (!child) continue;

    const { scheduled_start, scheduled_end, assigned_users } = child;

    if (!scheduled_start || !scheduled_end) {
      continue;
    }

    const start = new Date(scheduled_start);
    const end = new Date(scheduled_end);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      continue;
    }

    const durationMs = end.getTime() - start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);

    const wh = config.working_hours;
    let effectiveHours = durationHours;

    if (wh.overtime_enabled && durationHours > wh.overtime_threshold_hours) {
      const regularHours = wh.overtime_threshold_hours;
      const overtimeHours = durationHours - wh.overtime_threshold_hours;
      effectiveHours = regularHours + (overtimeHours * wh.overtime_multiplier);
    }

    // Multiply by number of assigned team members (man-hours)
    const teamMemberCount = Array.isArray(assigned_users) && assigned_users.length > 0 ? assigned_users.length : 1;
    totalManHours += effectiveHours * teamMemberCount;
  }

  if (totalManHours === 0) {
    return null;
  }

  return Math.round(totalManHours * 100) / 100;
}

export function calculateHybridEstimatedHours(
  config: EffortConfig,
  points: number | null | undefined,
  scheduledStart: string | null | undefined,
  scheduledEnd: string | null | undefined,
): number | null {
  const pointBasedHours = getEstimatedHoursForPoints(config, points);
  const scheduleBasedHours = calculateEstimatedHoursFromSchedule(config, scheduledStart, scheduledEnd);

  if (pointBasedHours === null && scheduleBasedHours === null) {
    return null;
  }

  if (pointBasedHours === null) {
    return scheduleBasedHours;
  }

  if (scheduleBasedHours === null) {
    return pointBasedHours;
  }

  const scheduleWeight = config.schedule_weight_in_hybrid;
  const hybridHours = (pointBasedHours * (1 - scheduleWeight)) + (scheduleBasedHours * scheduleWeight);

  return Math.round(hybridHours * 100) / 100;
}

export function applyIntensityMultipliers(
  config: EffortConfig,
  baseHours: number | null,
  selectedIntensityFactors?: Partial<Record<IntensityFactorKey, number>>,
): number | null {
  if (baseHours === null) {
    return null;
  }

  let totalMultiplier = 1;

  config.intensity_factors.forEach((factor) => {
    if (!factor.enabled) {
      return;
    }

    const userValue = selectedIntensityFactors?.[factor.key];
    if (userValue !== undefined && userValue > 0) {
      totalMultiplier *= (1 + ((userValue - 1) * factor.multiplier));
    }
  });

  return Math.round(baseHours * totalMultiplier * 100) / 100;
}

export function getEffectiveEstimatedHours(
  config: EffortConfig,
  points: number | null | undefined,
  scheduledStart: string | null | undefined,
  scheduledEnd: string | null | undefined,
  selectedIntensityFactors?: Partial<Record<IntensityFactorKey, number>>,
  children?: Array<{ scheduled_start?: string | null; scheduled_end?: string | null; assigned_users?: number[] | null } | null> | null,
): number | null {
  let baseHours: number | null = null;

  switch (config.calculation_mode) {
    case 'manual':
      return null;
    case 'point_based':
      baseHours = getEstimatedHoursForPoints(config, points);
      break;
    case 'schedule_based':
      // For jobs with child appointments, calculate from children (man-hours)
      if (children && children.length > 0) {
        baseHours = calculateEstimatedHoursFromChildren(config, children);
      } else {
        // Fallback to own schedule for single appointments or jobs without children
        baseHours = calculateEstimatedHoursFromSchedule(config, scheduledStart, scheduledEnd);
      }
      break;
    case 'hybrid':
      baseHours = calculateHybridEstimatedHours(config, points, scheduledStart, scheduledEnd);
      break;
  }

  if (baseHours === null) {
    return null;
  }

  return applyIntensityMultipliers(config, baseHours, selectedIntensityFactors);
}